import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { ChevronDown, LogOut, Settings, User } from 'lucide-react'
import type { ReactNode } from 'react'

interface UserMenuProps {
  avatarClassName?: string
  iconClassName?: string
  settingsHref?: string
  showSettings?: boolean
  roleBadge?: ReactNode
}

export default function UserMenu({
  avatarClassName = 'bg-primary/10',
  iconClassName = 'text-primary',
  settingsHref = '/settings',
  showSettings = true,
  roleBadge,
}: UserMenuProps) {
  const { t } = useTranslation()
  const { profile, signOut } = useAuth()
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  return (
    <div className="relative">
      <button
        onClick={() => setUserMenuOpen(!userMenuOpen)}
        className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted"
      >
        <div className={`w-8 h-8 rounded-full ${avatarClassName} flex items-center justify-center`}>
          {profile?.avatar_url ? (
            <img
              src={profile.avatar_url}
              alt=""
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <User className={`w-4 h-4 ${iconClassName}`} />
          )}
        </div>
        <span className="hidden md:block text-sm font-medium">
          {profile?.first_name}
        </span>
        <ChevronDown className="w-4 h-4 hidden md:block" />
      </button>

      {userMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            role="button"
            tabIndex={0}
            aria-label="Cerrar menú de usuario"
            onClick={() => setUserMenuOpen(false)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setUserMenuOpen(false) }}
          />
          <div className="absolute right-0 mt-2 w-48 bg-card rounded-lg shadow-lg border z-50">
            <div className="p-3 border-b">
              <p className="font-medium">
                {profile?.first_name} {profile?.last_name}
              </p>
              <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                {profile?.email}
              </p>
              {roleBadge}
            </div>
            <div className="p-2">
              {showSettings && (
                <Link
                  to={settingsHref}
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted"
                >
                  <Settings className="w-4 h-4" />
                  <span>{t('common.settings')}</span>
                </Link>
              )}
              <button
                onClick={() => {
                  setUserMenuOpen(false)
                  signOut()
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted w-full text-left text-red-600"
              >
                <LogOut className="w-4 h-4" />
                <span>{t('common.logout')}</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
